import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  hasUserMadeCookieChoice,
  acceptAllCookies,
  refuseAllCookies,
  saveCookiePreferences,
  getCookiePreferences,
  type CookiePreferences,
} from "@/lib/cookies";
import { Link } from "wouter";

/**
 * Cookie consent banner following Quebec's design system
 * Compliant with Loi 25 requirements
 */
export function CookieBanner() {
  const { t } = useTranslation("cookies");
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true, cannot be changed
    analytics: false,
    marketing: false,
    timestamp: Date.now(),
  });

  useEffect(() => {
    // Show banner if user hasn't made a choice
    const hasChoice = hasUserMadeCookieChoice();
    if (!hasChoice) {
      // Small delay to ensure page is loaded
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Load existing preferences
      const existing = getCookiePreferences();
      if (existing) {
        setPreferences(existing);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    acceptAllCookies();
    setIsVisible(false);
    setShowPreferences(false);
  };

  const handleRefuseAll = () => {
    refuseAllCookies();
    setIsVisible(false);
    setShowPreferences(false);
  };

  const handleSavePreferences = () => {
    saveCookiePreferences(preferences);
    setIsVisible(false);
    setShowPreferences(false);
  };

  const handleOpenPreferences = () => {
    setShowPreferences(true);
  };

  const handleClosePreferences = () => {
    setShowPreferences(false);
  };

  // Handle Escape key to close banner
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && (isVisible || showPreferences)) {
        handleRefuseAll();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isVisible, showPreferences]);

  if (!isVisible) return null;

  return (
    <>
      {/* Main Banner */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-gray-200 shadow-2xl"
        role="dialog"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-description"
      >
        <div className="container mx-auto px-4 py-6 md:py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Content Section */}
            <div className="flex-1 pr-4">
              <h2
                id="cookie-banner-title"
                className="text-lg font-semibold mb-2 text-gray-900"
              >
                {t("banner.title")}
              </h2>
              <p
                id="cookie-banner-description"
                className="text-sm text-gray-700 mb-2"
              >
                {t("banner.description")}{" "}
                <Link href="/privacy" className="text-primary underline hover:text-primary/80">
                  {t("privacy.linkText")}
                </Link>
                .
              </p>
            </div>

            {/* Buttons Section */}
            <div className="flex flex-col md:flex-row gap-3 md:items-center md:min-w-fit">
              {/* Desktop: Accept and Refuse on same line */}
              <div className="flex flex-col md:flex-row gap-3">
                <Button
                  onClick={handleAcceptAll}
                  variant="default"
                  className="w-full md:w-auto whitespace-nowrap"
                  size="default"
                >
                  {t("banner.accept")}
                </Button>
                <Button
                  onClick={handleRefuseAll}
                  variant="outline"
                  className="w-full md:w-auto whitespace-nowrap"
                  size="default"
                >
                  {t("banner.refuse")}
                </Button>
              </div>

              {/* Manage Preferences button */}
              <Button
                onClick={handleOpenPreferences}
                variant="ghost"
                className="w-full md:w-auto whitespace-nowrap text-sm"
                size="default"
              >
                {t("banner.manage")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preferences Modal */}
      <Dialog open={showPreferences} onOpenChange={handleClosePreferences}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">{t("preferences.title")}</DialogTitle>
            <DialogDescription className="text-base">
              {t("preferences.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Necessary Cookies - Always enabled */}
            <div className="flex items-start justify-between space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="necessary" className="text-base font-semibold">
                    {t("preferences.necessary.title")}
                  </Label>
                  <span className="text-sm text-gray-600 font-medium">
                    {t("preferences.necessary.required")}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {t("preferences.necessary.description")}
                </p>
              </div>
              <Switch
                id="necessary"
                checked={true}
                disabled={true}
                className="mt-1"
              />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg">
              <div className="flex-1">
                <Label htmlFor="analytics" className="text-base font-semibold">
                  {t("preferences.analytics.title")}
                </Label>
                <p className="text-sm text-gray-600 mt-2">
                  {t("preferences.analytics.description")}
                </p>
              </div>
              <Switch
                id="analytics"
                checked={preferences.analytics}
                onCheckedChange={(checked) =>
                  setPreferences((prev) => ({ ...prev, analytics: checked }))
                }
                className="mt-1"
              />
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg">
              <div className="flex-1">
                <Label htmlFor="marketing" className="text-base font-semibold">
                  {t("preferences.marketing.title")}
                </Label>
                <p className="text-sm text-gray-600 mt-2">
                  {t("preferences.marketing.description")}
                </p>
              </div>
              <Switch
                id="marketing"
                checked={preferences.marketing}
                onCheckedChange={(checked) =>
                  setPreferences((prev) => ({ ...prev, marketing: checked }))
                }
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <Button
              onClick={handleRefuseAll}
              variant="outline"
              className="w-full sm:w-auto"
            >
              {t("preferences.refuseAll")}
            </Button>
            <Button
              onClick={handleAcceptAll}
              variant="outline"
              className="w-full sm:w-auto"
            >
              {t("preferences.acceptAll")}
            </Button>
            <Button
              onClick={handleSavePreferences}
              className="w-full sm:w-auto"
            >
              {t("preferences.savePreferences")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
